/**
 * useUrlSync (B10) — deep links and history. Call once, from the app root.
 *
 *   URL → store  once, when `ready(state)` first holds (default: the dataset is in
 *                the store AND the chrome is up — phase explore / drive / attract —
 *                so a shared place never fights the intro's camera). Order: mood,
 *                then ?cat= (setCategory + bloom when no place), then ?place=
 *                (select, source "url"). Unknown ids are dropped and the URL is
 *                canonicalised with replaceState.
 *   store → URL  pushState when the selection changes (Back returns to the previous
 *                place, and on mobile the back gesture closes the card first);
 *                replaceState for chip and mood changes. Hover never writes.
 *                ?kiosk=1 is read-only: a kiosk never writes the URL.
 *   popstate     restores selection, category and mood (mood absent = day).
 *
 * Loop-free: writes are skipped while a URL state is being applied, and a write
 * only happens when the canonical search differs from location.search.
 * StrictMode-safe: the initial apply runs once per page load; every listener is
 * removed on unmount.
 */
import { useEffect, useRef } from "react";
import type { AppState, Phase } from "@/contracts/store";
import { actions, appStore, getApp } from "@/store/store";
import { DEFAULT_MOOD, historyMode, parseUrlState, sanitizeUrlState, serializeUrlState, urlStateOf, type UrlState } from "./urlState";
import { kioskFromSearch } from "@/features/layout/layoutMath";

export interface UrlSyncOptions {
  /** When the URL may be applied. Default: URL_READY.chrome. */
  ready?: (s: AppState) => boolean;
}

const CHROME_PHASES: ReadonlySet<Phase> = new Set<Phase>(["explore", "drive", "attract"]);

/** Readiness predicates for `ready`. */
export const URL_READY = {
  /** As soon as the dataset is in the store (harnesses without an intro). */
  data: (s: AppState): boolean => s.data !== null,
  /** Dataset loaded and the intro finished (the default). */
  chrome: (s: AppState): boolean => s.data !== null && CHROME_PHASES.has(s.phase),
} as const;

// Once per page load (StrictMode mounts effects twice; HMR re-runs them).
let initialApplied = false;
let applying = false;

/** Applies a (sanitised) URL state to the store. `restore` = popstate: absent keys clear. */
function applyState(u: UrlState, restore: boolean): void {
  const s = getApp();
  applying = true;
  try {
    const mood = u.mood ?? (restore ? DEFAULT_MOOD : null);
    if (mood && mood !== s.mood) actions.setMood(mood);

    // Back to a state without a selection: close the card before the chip changes.
    if (!u.place && restore && getApp().selectedId) actions.select(null, "url");

    if (u.cat) {
      if (getApp().category !== u.cat) actions.setCategory(u.cat);
      // A shared selection opens as the selection (the chip shows the category);
      // a shared chip opens as its bloom.
      if (!u.place && getApp().bloomCategory !== u.cat) actions.bloom(u.cat);
    } else if (restore && getApp().category) {
      actions.setCategory(null);
      if (getApp().bloomCategory) actions.bloom(null);
    }

    if (u.place && getApp().selectedId !== u.place) actions.select(u.place, "url");
  } finally {
    applying = false;
  }
}

function replaceSearch(search: string): void {
  history.replaceState(history.state, "", `${location.pathname}${search}${location.hash}`);
}

export function useUrlSync(opts: UrlSyncOptions = {}): void {
  // `ready` is read through a ref: a new inline function each render must not resubscribe.
  const readyRef = useRef(opts.ready ?? URL_READY.chrome);
  readyRef.current = opts.ready ?? URL_READY.chrome;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const kiosk = kioskFromSearch(location.search);

    const tryInitial = (s: AppState): void => {
      if (initialApplied || !readyRef.current(s)) return;
      initialApplied = true;
      applyState(sanitizeUrlState(parseUrlState(location.search), s.data), false);
      // canonical URL: invalid ids dropped, current store state reflected
      const next = serializeUrlState(location.search, urlStateOf(getApp()));
      if (!kiosk && next !== location.search) replaceSearch(next);
    };

    const write = (s: AppState, prev: AppState): void => {
      if (kiosk || applying) return;
      if (s.selectedId === prev.selectedId && s.category === prev.category && s.mood === prev.mood) return;
      const next = serializeUrlState(location.search, urlStateOf(s));
      const mode = historyMode(prev, s, location.search, next);
      if (!mode) return;
      const url = `${location.pathname}${next}${location.hash}`;
      if (mode === "push") history.pushState(null, "", url);
      else history.replaceState(history.state, "", url);
    };

    const unsub = appStore.subscribe((s, prev) => {
      if (!initialApplied) tryInitial(s);
      else write(s, prev);
    });
    tryInitial(getApp());

    const onPop = (): void => {
      if (!initialApplied) return;
      const u = sanitizeUrlState(parseUrlState(location.search), getApp().data);
      applyState(u, true);
      // A hand-edited or stale entry: show what the app now shows.
      const next = serializeUrlState(location.search, urlStateOf(getApp()));
      if (!kiosk && next !== location.search) replaceSearch(next);
    };
    window.addEventListener("popstate", onPop);
    return () => {
      unsub();
      window.removeEventListener("popstate", onPop);
    };
  }, []);
}
